import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '@/lib/api';
import {
    Radio, Trophy, Target, Sparkles, ArrowRight, Clock, Loader2, RefreshCw,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const euro = (n) => new Intl.NumberFormat('fr-FR', {
    style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
}).format(n || 0);

const fmtRelative = (iso) => {
    if (!iso) return '—';
    try {
        const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (diff < 60) return "à l'instant";
        if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
        if (diff < 86400) return `il y a ${Math.floor(diff / 3600)} h`;
        const days = Math.floor(diff / 86400);
        if (days < 7) return `il y a ${days} j`;
        return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return '—'; }
};

const WINDOW_OPTIONS = [
    { value: 7, label: '7 derniers jours' },
    { value: 30, label: '30 derniers jours' },
    { value: 90, label: '90 derniers jours' },
];

// Un même dossier peut apparaître 2 fois (EP + Travaux d’optimisation) → clé composite.
const rowKey = (r) => `${r.prospect_id}-${r.pipeline}`;

function CommissionRow({ item, mode, onOpen }) {
    const isEP = item.pipeline === 'easy_power';
    const isWon = mode === 'won';
    return (
        <button
            type="button"
            data-testid={`parrain-feed-row-${mode}-${rowKey(item)}`}
            onClick={() => onOpen(item.prospect_id, item.pipeline)}
            className={`w-full text-left rounded-xl border p-3 transition-all hover:shadow-sm ${
                isWon
                    ? 'border-emerald-100 bg-white hover:border-emerald-300 hover:bg-emerald-50/30'
                    : 'border-amber-100 bg-white hover:border-amber-300 hover:bg-amber-50/30'
            }`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-900 truncate max-w-[180px] sm:max-w-none">
                            {item.company_name}
                        </span>
                        <Badge className={`shrink-0 ${isEP ? 'bg-purple-100 text-purple-700' : 'bg-amber-100 text-amber-700'} hover:opacity-90`}>
                            {isEP ? 'Courtage' : 'Travaux d’optimisation'}
                        </Badge>
                        {item.level && (
                            <Badge className="shrink-0 bg-indigo-100 text-indigo-700 border border-indigo-200 hover:opacity-90">
                                N{item.level}
                            </Badge>
                        )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500">
                        <span>{item.stage_label}</span>
                        <span className="inline-flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {isWon ? `gagné ${fmtRelative(item.won_at)}` : `depuis ${item.days_in_stage != null ? `${item.days_in_stage} j` : '—'}`}
                        </span>
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Ma part</p>
                    <p className={`text-lg font-bold ${isWon ? 'text-emerald-600' : 'text-amber-600'}`}>
                        {euro(item.my_amount)}
                    </p>
                </div>
            </div>
        </button>
    );
}

export default function ParrainCommissionFeed({ user }) {
    const navigate = useNavigate();
    const [windowDays, setWindowDays] = useState(30);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(false);
    const [pulse, setPulse] = useState(false);
    // Compte les items connus pour détecter une nouveauté (feedback visuel)
    const prevWonKeys = useRef(new Set());
    const isMounted = useRef(true);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true); else setRefreshing(true);
        try {
            const res = await dashboardAPI.getParrainFeed({ window_days: windowDays, limit: 20 });
            if (!isMounted.current) return;
            const nextData = res.data;
            // Détecte l'arrivée de nouveaux "gagnés" pour flasher le bloc
            const newKeys = new Set((nextData?.recently_won?.items || []).map(rowKey));
            const hasNew = [...newKeys].some((k) => !prevWonKeys.current.has(k));
            if (hasNew && prevWonKeys.current.size > 0) {
                setPulse(true);
                setTimeout(() => setPulse(false), 2000);
            }
            prevWonKeys.current = newKeys;
            setData(nextData);
            setError(false);
        } catch (e) {
            if (!isMounted.current) return;
            console.error('parrain feed load error', e);
            setError(true);
        } finally {
            if (!isMounted.current) return;
            setLoading(false);
            setRefreshing(false);
        }
    }, [windowDays]);

    useEffect(() => {
        isMounted.current = true;
        load(false);
        // Polling toutes les 30 s (silencieux) — s'arrête si l'onglet passe en arrière-plan
        // pour éviter la charge inutile ; reprend au retour au premier plan.
        const tick = () => { if (document.visibilityState === 'visible') load(true); };
        const id = setInterval(tick, 30000);
        const onVis = () => { if (document.visibilityState === 'visible') load(true); };
        document.addEventListener('visibilitychange', onVis);
        return () => {
            isMounted.current = false;
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVis);
        };
    }, [load]);

    const openDossier = useCallback((id, pipeline) => {
        navigate(`/prospects/${id}${pipeline ? `?module=${pipeline}` : ''}`);
    }, [navigate]);

    if (user?.role !== 'parrain') return null;

    const won = data?.recently_won || { items: [], count: 0, total_amount: 0 };
    const chase = data?.to_chase || { items: [], count: 0, total_amount: 0 };

    return (
        <Card
            data-testid="parrain-commission-feed"
            className={`border-indigo-200 overflow-hidden transition-shadow duration-500 ${pulse ? 'ring-2 ring-emerald-300 shadow-lg' : ''}`}
        >
            <CardContent className="p-4 sm:p-5">
                {/* Header */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2 min-w-0">
                        <div className="relative">
                            <div className="p-2 rounded-full bg-indigo-100">
                                <Radio className="h-4 w-4 text-indigo-600" />
                            </div>
                            <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 animate-pulse" />
                        </div>
                        <div className="min-w-0">
                            <h3 className="text-base font-semibold text-slate-900" data-testid="parrain-feed-title">
                                Mon fil de commissions
                            </h3>
                            <p className="text-xs text-slate-500">
                                Temps réel — ce qui vient de closer et ce qu&apos;il reste à aller chercher
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Select value={String(windowDays)} onValueChange={(v) => setWindowDays(Number(v))}>
                            <SelectTrigger className="h-8 w-[170px] text-xs" data-testid="parrain-feed-window-select">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {WINDOW_OPTIONS.map((o) => (
                                    <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {(refreshing || loading) && (
                            <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" data-testid="parrain-feed-refreshing" />
                        )}
                    </div>
                </div>

                {error && !loading ? (
                    <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                        <RefreshCw className="h-4 w-4" />
                        Impossible de charger le fil. Nouvelle tentative dans 30 s.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Récemment gagnées */}
                        <div
                            data-testid="parrain-feed-won-block"
                            className="rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-3 sm:p-4"
                        >
                            <div className="flex items-center justify-between gap-2 mb-3">
                                <div className="flex items-center gap-2">
                                    <Trophy className="h-4 w-4 text-emerald-600" />
                                    <span className="text-sm font-semibold text-emerald-800">
                                        Récemment gagnées
                                    </span>
                                    <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 hover:opacity-90" data-testid="parrain-feed-won-count">
                                        {won.count}
                                    </Badge>
                                </div>
                                <span className="text-sm font-bold text-emerald-700" data-testid="parrain-feed-won-total">
                                    {euro(won.total_amount)}
                                </span>
                            </div>
                            <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1" data-testid="parrain-feed-won-list">
                                {loading ? (
                                    [1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)
                                ) : won.items.length === 0 ? (
                                    <div className="text-center py-6 text-slate-500">
                                        <Sparkles className="h-6 w-6 mx-auto mb-1.5 text-slate-300" />
                                        <p className="text-xs" data-testid="parrain-feed-won-empty">
                                            Rien de fraîchement gagné sur cette période. Reste connecté !
                                        </p>
                                    </div>
                                ) : (
                                    won.items.map((it) => (
                                        <CommissionRow key={rowKey(it)} item={it} mode="won" onOpen={openDossier} />
                                    ))
                                )}
                            </div>
                        </div>

                        {/* À aller chercher */}
                        <div
                            data-testid="parrain-feed-chase-block"
                            className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-3 sm:p-4"
                        >
                            <div className="flex items-center justify-between gap-2 mb-3">
                                <div className="flex items-center gap-2">
                                    <Target className="h-4 w-4 text-amber-600" />
                                    <span className="text-sm font-semibold text-amber-800">
                                        À aller chercher
                                    </span>
                                    <Badge className="bg-amber-100 text-amber-700 border border-amber-200 hover:opacity-90" data-testid="parrain-feed-chase-count">
                                        {chase.count}
                                    </Badge>
                                </div>
                                <span className="text-sm font-bold text-amber-700" data-testid="parrain-feed-chase-total">
                                    {euro(chase.total_amount)}
                                </span>
                            </div>
                            <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1" data-testid="parrain-feed-chase-list">
                                {loading ? (
                                    [1, 2, 3].map((i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)
                                ) : chase.items.length === 0 ? (
                                    <div className="text-center py-6 text-slate-500">
                                        <ArrowRight className="h-6 w-6 mx-auto mb-1.5 text-slate-300" />
                                        <p className="text-xs" data-testid="parrain-feed-chase-empty">
                                            Aucune commission en cours à relancer.
                                        </p>
                                    </div>
                                ) : (
                                    chase.items.map((it) => (
                                        <CommissionRow key={rowKey(it)} item={it} mode="chase" onOpen={openDossier} />
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
